import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { partidoInfoService } from '@/services/PartidoInfoService';
import { PartidoInfo } from '@/types/partido';
import { Categoria } from '@/types/enums';

interface UsePartidoInfoResult {
  proximoPartido: PartidoInfo | null;
  partidosPorCategoria: PartidoInfo[];
  todosLosPartidos: PartidoInfo[];
  loading: boolean;
  error: string | null;
  refetchProximoPartido: () => Promise<void>;
  refetchPartidosPorCategoria: (categoria: Categoria) => Promise<void>;
  refetchTodosLosPartidos: () => Promise<void>;
  fetchPartidoPorEntrenador: (entrenadorId: number) => Promise<void>;
}

/**
 * Hook para gestionar información de partidos del web scraping
 * Optimizado con React Query para cacheo de datos
 * @returns Objeto con información de partidos y funciones de control
 */
export const usePartidoInfo = (): UsePartidoInfoResult => {
  // Estados locales para las peticiones bajo demanda (no cacheadas automáticamente aún)
  const [partidosPorCategoria, setPartidosPorCategoria] = useState<PartidoInfo[]>([]);
  const [todosLosPartidos, setTodosLosPartidos] = useState<PartidoInfo[]>([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  
  // Estado local para "Partido por Entrenador" que reutilizaba proximoPartido
  const [partidoEntrenador, setPartidoEntrenador] = useState<PartidoInfo | null>(null);

  // 1. Query para el Próximo Partido (Cacheada 24h)
  const { 
    data: proximoPartido, 
    isLoading: isLoadingProximo, 
    isFetching: isFetchingProximo,
    error: errorProximo,
    refetch: refetchProximo
  } = useQuery({
    queryKey: ['partido-info', 'proximo'],
    queryFn: async () => {
      const response = await partidoInfoService.obtenerProximoPartido();
      if (!response.success) {
        throw new Error(response.message || 'No se encontró información del próximo partido');
      }
      return response.data || null;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 horas de validez
    gcTime: 1000 * 60 * 60 * 24,    // Mantener en memoria 24 horas
    retry: 1
  });

  /**
   * Wrapper para refetch compatible con la interfaz anterior
   */
  const refetchProximoPartido = async () => {
    await refetchProximo();
  };

  /**
   * Obtiene partidos por categoría
   */
  const fetchPartidosPorCategoria = async (cat: Categoria) => {
    try {
      setManualLoading(true);
      setManualError(null);
      
      const response = await partidoInfoService.obtenerPartidosPorCategoria(cat);
      
      if (response.success && response.data) {
        setPartidosPorCategoria(response.data);
      } else {
        setManualError(response.message || 'No se encontraron partidos para esta categoría');
        setPartidosPorCategoria([]);
      }
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Error desconocido');
      setPartidosPorCategoria([]);
    } finally {
      setManualLoading(false);
    }
  };

  /**
   * Obtiene todos los partidos
   */
  const fetchTodosLosPartidos = async () => {
    try {
      setManualLoading(true);
      setManualError(null);
      
      const response = await partidoInfoService.obtenerTodosLosPartidos();
      
      if (response.success && response.data) {
        setTodosLosPartidos(response.data);
      } else {
        setManualError(response.message || 'No se encontraron partidos');
        setTodosLosPartidos([]);
      }
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Error desconocido');
      setTodosLosPartidos([]);
    } finally {
      setManualLoading(false);
    }
  };

  /**
   * Obtiene el partido del entrenador
   * Nota: Antes sobreescribía proximoPartido. Ahora usa un estado local 'partidoEntrenador'
   * Si se necesita que sobreescriba la UI principal, habría que ajustar la lógica de visualización.
   * Por compatibilidad, si hay partidoEntrenador, lo devolvemos como proximoPartido.
   */
  const fetchPartidoPorEntrenador = async (entrenadorId: number) => {
    try {
      setManualLoading(true);
      setManualError(null);
      
      const response = await partidoInfoService.obtenerPartidoPorEntrenador(entrenadorId);
      
      if (response.success && response.data) {
        setPartidoEntrenador(response.data);
      } else {
        setManualError(response.message || 'No se encontró partido para el entrenador');
        setPartidoEntrenador(null);
      }
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Error desconocido');
      setPartidoEntrenador(null);
    } finally {
      setManualLoading(false);
    }
  };

  // Lógica combinada para el retorno
  const finalProximoPartido = partidoEntrenador || proximoPartido || null;
  const finalLoading = isLoadingProximo || isFetchingProximo || manualLoading;
  const finalError = manualError || (errorProximo as Error)?.message || null;

  return {
    proximoPartido: finalProximoPartido,
    partidosPorCategoria,
    todosLosPartidos,
    loading: finalLoading,
    error: finalError,
    refetchProximoPartido,
    refetchPartidosPorCategoria: fetchPartidosPorCategoria,
    refetchTodosLosPartidos: fetchTodosLosPartidos,
    fetchPartidoPorEntrenador
  };
};
